/**
 * CampaignRoomView — Sprint 4 · Project Symphony
 *
 * The operational headquarters. A single scrollable executive briefing
 * composed from the read-only Campaign Room payload — no tabs, no
 * regeneration, no AI calls. Sections flow top-to-bottom exactly in
 * the order the executive directive mandates.
 *
 * Patch 16 addition: anchor-based URL continuity. Each section carries
 * an id, and a sticky jump nav updates the URL hash as the visitor
 * scrolls. Browser Back/Forward moves between sections; refresh
 * preserves the section; invalid hashes fall back safely — no full-page
 * remounts, no artificial tabs on a single-scroll layout.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useSyncCampaignMission } from "@/entities/missions";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useAuthStore } from "@/entities/auth";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getCampaignRoom } from "../api";
import {
  AssetsInProduction,
  BeginProductionCTA,
  ProductionProgress,
  SUBSTATE_LABEL,
  useGenerationState,
} from "@/features/campaign-generation";
import { EditorialCalendarSection } from "@/features/editorial-calendar";
import { AutopilotSection, MorningBriefCard, WeeklyBriefCard } from "@/features/autopilot";
import { ExecutiveHeader } from "./sections/ExecutiveHeader";
import { DepartmentStatusPanel } from "./sections/DepartmentStatusPanel";
import { CampaignTimeline } from "./sections/CampaignTimeline";
import { StrategyRecap } from "./sections/StrategyRecap";
import { ContentPlanSection } from "./sections/ContentPlanSection";
import { VaultSummary } from "./sections/VaultSummary";
import { PublishingOverviewSection } from "./sections/PublishingOverviewSection";
import { ActivityFeed } from "./sections/ActivityFeed";
import { PerformancePlaceholder } from "./sections/PerformancePlaceholder";
import { CampaignHealthPanel } from "@/widgets/CampaignHealthPanel/CampaignHealthPanel";
import { HorizontalScrollCue } from "@/shared/ui/horizontal-scroll-cue";

/** Anchor definitions for the sticky jump nav. Order matches the visual
 *  reading order. Invalid hashes silently fall through to "overview". */
const SECTIONS: Array<{ id: string; label: string }> = [
  { id: "overview",   label: "Overview"   },
  { id: "brief",      label: "Your morning" },
  { id: "autopilot",  label: "Autopilot"  },
  { id: "production", label: "Production" },
  { id: "assets",     label: "Assets"     },
  { id: "editorial",  label: "Editorial"  },
  { id: "timeline",   label: "Timeline"   },
  { id: "strategy",   label: "Strategy"   },
  { id: "content",    label: "Content"    },
  { id: "publishing", label: "Publishing" },
  { id: "activity",   label: "Activity"   },
  { id: "performance", label: "Performance" },
];
const SECTION_IDS = SECTIONS.map((s) => s.id);
const DEFAULT_SECTION = SECTIONS[0].id;

function safeSectionFromHash(): string {
  if (typeof window === "undefined") return DEFAULT_SECTION;
  const raw = window.location.hash.replace(/^#/, "").toLowerCase();
  return SECTION_IDS.includes(raw) ? raw : DEFAULT_SECTION;
}

/** Sticky in-page jump nav — anchor navigation with proper history. */
function SectionJumpNav({
  active, onJump,
}: { active: string; onJump: (id: string) => void }): JSX.Element {
  return (
    <nav
      data-testid="campaign-room-jump-nav"
      aria-label="Campaign Room sections"
      className="sticky top-16 z-20 -mx-4 mb-6 border-y border-border/40 bg-background/85 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8"
    >
      <HorizontalScrollCue testId="campaign-room-sections-scroll">
      <div className="flex w-max min-w-full gap-1 scroll-smooth">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`campaign-room-jump-${s.id}`}
            aria-current={active === s.id ? "location" : undefined}
            onClick={() => onJump(s.id)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition ${
              active === s.id
                ? "bg-amber-500/15 text-foreground ring-1 ring-amber-500/30"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      </HorizontalScrollCue>
    </nav>
  );
}

export function CampaignRoomView(): JSX.Element {
  const params = useParams<{ blueprintId: string }>();
  const blueprintId = params.blueprintId ?? "";
  const navigate = useNavigate();

  // Milestone 37.2 · keep the Campaign Focus Flow mission aligned
  // with reality on every entry to the room. Never mutates campaign
  // business state; fire-and-forget.
  const workspace = useCurrentWorkspace();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const syncMission = useSyncCampaignMission(workspace?.id ?? null, userId);
  useEffect(() => {
    if (!blueprintId || !workspace?.id) return;
    syncMission.mutate(blueprintId);
    // Only sync on blueprint/workspace change — not on mutation reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprintId, workspace?.id]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["campaign-room", blueprintId],
    queryFn: () => getCampaignRoom(blueprintId),
    enabled: Boolean(blueprintId),
    refetchInterval: 30_000,
  });

  const genState = useGenerationState(blueprintId, {
    activeStatus: data?.campaign_status.code,
    enabled: Boolean(data),
  });

  const decoratedDepartments = data
    ? data.departments.map((row) => {
        const subMap: Record<string, string | undefined> = {
          content_team: genState.data?.department_substates.content_team.substate,
          creative_studio: genState.data?.department_substates.creative_studio.substate,
          publishing_team: genState.data?.department_substates.publishing_team.substate,
        };
        const code = subMap[row.key];
        if (!code) return row;
        return { ...row, detail: SUBSTATE_LABEL[code as keyof typeof SUBSTATE_LABEL] ?? row.detail };
      })
    : [];

  // ── URL-hash driven active section ─────────────────────────────
  const [activeSection, setActiveSection] = useState<string>(() => safeSectionFromHash());
  // We want the browser back button to move between sections. Because
  // useNavigate on the same route with a new hash pushes a new entry,
  // we get real history support with no full-page remount.
  const jumpTo = useCallback((id: string) => {
    if (!SECTION_IDS.includes(id)) return;
    navigate({ hash: `#${id}` }, { replace: false });
    // Scroll the target into view. scrollMarginTop on each section
    // keeps the sticky nav from covering the heading.
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [navigate]);

  // Sync activeSection with the URL hash on back/forward + on refresh.
  useEffect(() => {
    const handler = () => setActiveSection(safeSectionFromHash());
    window.addEventListener("hashchange", handler);
    // If we mounted with a valid hash, jump there once the layout paints.
    const initial = safeSectionFromHash();
    if (initial !== DEFAULT_SECTION) {
      const t = window.setTimeout(() => {
        const el = document.getElementById(initial);
        if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
      }, 60);
      return () => { window.clearTimeout(t); window.removeEventListener("hashchange", handler); };
    }
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  // Scroll-spy: whichever section is closest to the top wins.
  const sectionsRef = useRef<Record<string, HTMLElement | null>>({});
  useEffect(() => {
    if (typeof window === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to (but past) the top boundary.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id;
          if (SECTION_IDS.includes(id)) setActiveSection((prev) => (prev === id ? prev : id));
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        sectionsRef.current[id] = el;
        io.observe(el);
      }
    });
    return () => io.disconnect();
  }, [data]);

  // Section anchor styling — accessible landmark IDs + scroll offset.
  const anchor = useMemo(() => ({
    style: { scrollMarginTop: 120 } as React.CSSProperties,
  }), []);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[70vh]"
        data-testid="campaign-room-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className="mx-auto max-w-2xl px-6 py-24 text-center"
        data-testid="campaign-room-error"
      >
        <h2 className="text-2xl font-semibold mb-3">We couldn&apos;t load this campaign.</h2>
        <p className="text-muted-foreground">Return to Home and try again.</p>
      </div>
    );
  }

  const bp = data.blueprint;
  if (bp.status === "draft" || bp.status === "planned") {
    navigate(`/campaigns/${blueprintId}/strategy`, { replace: true });
    return <div data-testid="campaign-room-redirecting" />;
  }

  return (
    <div
      className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6 pb-24"
      data-testid="campaign-room-view"
    >
      {data.ai_studio_path ? (
        <div className="mb-4 flex justify-end">
          <Link
            to={data.ai_studio_path}
            data-testid={`campaign-room-open-ai-studio-${data.campaign_pack_id}`}
            className="inline-flex items-center rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-amber-400/20"
          >
            Open generated assets in AI Studio →
          </Link>
        </div>
      ) : null}
      <SectionJumpNav active={activeSection} onJump={jumpTo} />

      <div className="space-y-10">
        <section id="overview" data-testid="campaign-room-section-overview" style={anchor.style}>
          <ExecutiveHeader
            blueprint={bp}
            campaignStatus={data.campaign_status}
          />
          {/* Patch 34.2.1d · canonical CampaignRun health, resolved via
              blueprint → run lookup. Never auto-heals; only reports. */}
          <div className="mt-6">
            <CampaignHealthPanel blueprintId={blueprintId} />
          </div>
        </section>

        <section id="brief" data-testid="campaign-room-section-brief" style={anchor.style}>
          <MorningBriefCard />
        </section>

        <section id="autopilot" data-testid="campaign-room-section-autopilot" style={anchor.style}>
          <AutopilotSection />
        </section>

        <section id="production" data-testid="campaign-room-section-production" style={anchor.style}>
          <ProductionProgress
            state={genState.data}
            campaignStatusCode={data.campaign_status.code}
          />
          <div className="mt-6">
            <BeginProductionCTA
              blueprintId={blueprintId}
              totalAssets={data.content_plan.total}
              campaignStatusCode={data.campaign_status.code}
            />
          </div>
          <div className="mt-6">
            <DepartmentStatusPanel rows={decoratedDepartments} />
          </div>
        </section>

        {(data.campaign_status.code === "generating" ||
          data.campaign_status.code === "generation_complete") && (
          <section id="assets" data-testid="campaign-room-section-assets" style={anchor.style}>
            <AssetsInProduction state={genState.data} />
          </section>
        )}

        <section id="editorial" data-testid="campaign-room-section-editorial" style={anchor.style}>
          <EditorialCalendarSection
            blueprintId={blueprintId}
            campaignStatusCode={data.campaign_status.code}
          />
        </section>

        <section id="timeline" data-testid="campaign-room-section-timeline" style={anchor.style}>
          <CampaignTimeline events={data.timeline} />
        </section>

        <section id="strategy" data-testid="campaign-room-section-strategy" style={anchor.style}>
          <StrategyRecap blueprint={bp} />
        </section>

        <section id="content" data-testid="campaign-room-section-content" style={anchor.style}>
          <ContentPlanSection plan={data.content_plan} />
          <div className="mt-6">
            <VaultSummary content={bp.content_strategy!} />
          </div>
        </section>

        <section id="publishing" data-testid="campaign-room-section-publishing" style={anchor.style}>
          <PublishingOverviewSection overview={data.publishing_overview} />
        </section>

        <section id="activity" data-testid="campaign-room-section-activity" style={anchor.style}>
          <ActivityFeed events={data.timeline} />
          <div className="mt-6">
            <WeeklyBriefCard />
          </div>
        </section>

        <section id="performance" data-testid="campaign-room-section-performance" style={anchor.style}>
          <PerformancePlaceholder />
        </section>
      </div>
    </div>
  );
}
